/**
 * lib
 */
import { jwtDecode } from "jwt-decode";

/**
 * interfaces
 */
import { IJwtPayload } from '@core/utilities';

/**
 * JWT Utilities Class
 * @class JwtUtils
 * @description Provides utility methods for JWT token operations including decoding,
 * validation, and expiration checking. This class wraps JWT tokens and provides
 * convenient methods for token manipulation and validation.
 * 
 * @template T - The type of JWT payload (defaults to IJwtPayload)
 * 
 * @example
 * ```typescript
 * // Create JWT utils instance
 * const jwtUtils = new JwtUtils('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
 * 
 * // Decode token payload
 * const payload = jwtUtils.payload();
 * 
 * // Check if token is expired or will expire soon
 * const isExpiredSoon = jwtUtils.isExpiredOrSkew(300); // 5 minutes threshold
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export class JwtUtils<T = IJwtPayload> {

    /**
     * Creates a new JwtUtils instance
     * @constructor
     * @param {string} token - The JWT token string to wrap
     * @throws {Error} Throws error if token is null or undefined
     * @description Initializes the JwtUtils instance with a JWT token.
     * The token is validated during construction and an error is thrown
     * if the token is null or undefined.
     */
    constructor(public readonly token: string) {
        
        if (token == null) {
            throw new Error(`arg token is ${token}`)
        }

    }

    /**
     * Decodes the JWT token and returns its payload
     * @public
     * @returns {T} The decoded JWT payload
     * @description Decodes the JWT token using jwt-decode library and returns
     * the payload as the specified generic type T.
     * 
     * @example
     * ```typescript
     * const jwtUtils = new JwtUtils(token);
     * const payload = jwtUtils.payload();
     * console.log(payload.sub); // User ID
     * console.log(payload.exp); // Expiration timestamp
     * ```
     */
    public payload(): T | null {
        try {
            return jwtDecode<T>(this.token)
        }
        catch (error) {
            console.warn('Invalid token or cannot decode JWT payload:', error);
            return null;
        }
    }

    /**
     * Checks if the token is expired or will expire within a given threshold
     * @public
     * @param {number} skewSeconds - Number of seconds before actual expiry to consider as "expired soon" (default: 300)
     * @returns {boolean} True if token is expired or will expire within the threshold, false otherwise
     * @description Validates token expiration with a configurable skew time. Returns true if the token
     * is already expired or will expire within the specified number of seconds.
     * Handles invalid tokens gracefully by returning true for safety.
     * 
     * @example
     * ```typescript
     * const jwtUtils = new JwtUtils(token);
     * 
     * // Check if token expires within 5 minutes
     * const expiresSoon = jwtUtils.isExpiredOrSkew(300);
     * 
     * // Check if token is already expired
     * const isExpired = jwtUtils.isExpiredOrSkew(0);
     * ```
     */
    public isExpiredOrSkew(skewSeconds: number = 0): boolean {

        // if token is null, return true
        if (!this.token) return true;
    
        try {
            // decode the token
            const { exp } = this.payload() as IJwtPayload;

            // if expiration time is not found, return true
            if (!exp) return true;

            // get the current time in seconds
            const _maxExpiryTime = Math.floor(Date.now() / 1000) + skewSeconds;

            // get the expiration time
            // check if the token is expired or will expire within the given threshold
            return exp <= _maxExpiryTime;
        }
        catch (error) {
            console.warn('Invalid token or cannot decode JWT exp:', error);
            return true;
        }
    }

    /**
     * Gets the remaining validity time of the token in milliseconds.
     * 
     * @public
     * @param {number} bufferSeconds - Safety buffer in seconds to subtract from the remaining time (default: 5).
     *                                 Useful to ensure token is considered "expired" slightly before the actual exp time.
     * @returns {number} The remaining time in milliseconds. Returns 0 if token is invalid or expired.
     * 
     * @example
     *
     * const jwtUtils = new JwtUtils(token);
     * // Get time until expiry (minus 5s buffer)
     * const timeToRefresh = jwtUtils.getRemainingTimeInMs(5);
     * 
     * if (timeToRefresh > 0) {
     *   setTimeout(() => refresh(), timeToRefresh);
     * }
     * 
     */
    public getRemainingTimeInMs(bufferSeconds: number = 5): number {
        try {

            // ES6 Destruction
            // decode the token
            const { exp } = this.payload() as IJwtPayload || {};

            // if expiration time is not found, return 0
            if (!exp) return 0;

            // get the current timestamp in seconds
            const _currentTimestamp = Math.floor(Date.now() / 1000);

            // get the remaining time
            const _remainingTime = (exp - _currentTimestamp - bufferSeconds) * 1000;
            
            // return the remaining time in milliseconds
            return Math.max(0, _remainingTime);

        } catch (error) {
            console.warn('Invalid token or cannot decode JWT exp:', error);
            return 0;
        }
    }
  
}
